import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUp, signInWithRedirect } from "aws-amplify/auth";
import { toast } from "sonner";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CountryCodeSelect, COUNTRIES, type Country } from "@/components/CountryCodeSelect";
import { cn } from "@/lib/utils";
import { usePageTitle } from "@/hooks/usePageTitle";
import { isReleaseFlagOn } from "@/config/featureFlags";
import type { MinorConsentPrefill } from "./ConsentFormPage";

const DEFAULT_COUNTRY: Country = COUNTRIES.find(c => c.code === "IN") ?? COUNTRIES[0];

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

// DPDP Act 2023 s.9 — under-13 signups require parental consent instead of
// direct Cognito account creation. Mirrors ConsentFormPage's isUnder13.
function isUnder13(dob: string): boolean {
  if (!dob) return false;
  const birth = new Date(dob);
  const today = new Date();
  const thirteenth = new Date(birth.getFullYear() + 13, birth.getMonth(), birth.getDate());
  return today < thirteenth;
}

// Must match the Cognito user pool's actual password policy
// (infra-aws env/dev/terraform.tfvars: cognito_password_minimum_length=10,
// require_lowercase/uppercase/numbers=true, require_symbols=false). There is
// no API to read this policy at runtime — if it changes in Terraform, this
// must be updated to match.
function passwordIssues(password: string): string | null {
  if (password.length < 10) return "Password must be at least 10 characters";
  if (!/[a-z]/.test(password)) return "Password must include a lowercase letter";
  if (!/[A-Z]/.test(password)) return "Password must include an uppercase letter";
  if (!/[0-9]/.test(password)) return "Password must include a digit";
  return null;
}

// Full name: letters (any script), spaces, apostrophes, hyphens and full stops
// ("O'Brien", "Mary-Jane", "J. Smith"). Digits and other symbols are stripped.
const NAME_DISALLOWED = /[0-9!@#$%^&*()_+=[\]{};:"\\|,<>/?~`€£¥₹§±¿¡]/g;
const NAME_HAS_LETTER = /[^\s'.-]/;

export default function RegisterPage() {
  usePageTitle('Create Account | BookMyCoaching');
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [fullNameNote, setFullNameNote] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [country, setCountry] = useState<Country>(DEFAULT_COUNTRY);
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Read at render time (not module scope) so it follows the env.js / test mock. ADR-162.
  const dobOn = isReleaseFlagOn("REGISTRATION_DOB");

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!NAME_HAS_LETTER.test(fullName)) next.fullName = "Full name is required";
    if (dobOn) {
      if (!dateOfBirth) {
        next.dateOfBirth = "Date of birth is required";
      } else if (new Date(dateOfBirth) >= new Date()) {
        next.dateOfBirth = "Date of birth must be in the past";
      }
    }
    if (!mobileNumber.trim()) next.mobileNumber = "Mobile number is required";
    if (!email.trim()) next.email = "Email address is required";
    else if (!isValidEmail(email.trim())) next.email = "Enter a valid email address";
    const passwordError = passwordIssues(password);
    if (passwordError) next.password = passwordError;
    if (confirmPassword !== password) next.confirmPassword = "Passwords do not match";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    // Under-13 — do not create a Cognito account at all; route to the
    // parental-consent flow instead (DPDP Act 2023 s.9).
    if (dobOn && isUnder13(dateOfBirth)) {
      // Carry what they've typed so the consent form opens pre-filled. Router
      // state, not query params — a minor's details must not end up in the URL.
      const prefill: MinorConsentPrefill = { childName: fullName.trim(), childEmail: email.trim(), dateOfBirth };
      navigate("/age-gate", { state: prefill });
      return;
    }

    setSubmitting(true);
    try {
      const { nextStep } = await signUp({
        username: email.trim(),
        password,
        options: {
          userAttributes: {
            name: fullName.trim(),
            // Omit the key entirely when the flag is OFF: Cognito rejects an empty/invalid birthdate.
            ...(dobOn ? { birthdate: dateOfBirth } : {}),
            phone_number: `${country.dial}${mobileNumber.trim()}`,
          },
          autoSignIn: true,
        },
      });

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP" || nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        navigate("/confirm-signup", { state: { email: email.trim() } });
        return;
      }
      // signUpStep === 'DONE' — account already confirmed (unexpected for
      // email/password with verification required, but handle gracefully).
      navigate("/login");
    } catch (err: unknown) {
      const name = err instanceof Error ? err.name : "";
      const message = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      if (name === "UsernameExistsException") {
        setErrors(prev => ({ ...prev, email: "An account with this email already exists" }));
      } else if (name === "InvalidPasswordException") {
        setErrors(prev => ({ ...prev, password: message }));
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignUp = async () => {
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not start Google sign-up";
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <div className="w-48 sm:w-56">
            <Logo size="lg" className="w-full" />
          </div>
        </div>

        <div className="mb-8 text-center">
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-foreground mb-2 leading-snug">
            Create your account
          </h1>
          <p className="text-muted-foreground text-sm">
            Join BookMyCoaching as a teacher or student
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-sm font-medium">
              Full Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={e => {
                const cleaned = e.target.value.replace(NAME_DISALLOWED, "");
                setFullNameNote(cleaned !== e.target.value ? "Name can only contain letters" : "");
                setFullName(cleaned);
              }}
              placeholder="Your full name"
              autoComplete="name"
              aria-invalid={!!errors.fullName}
              className={cn("h-11", errors.fullName && "border-destructive focus-visible:ring-destructive")}
            />
            {errors.fullName
              ? <p className="text-xs text-destructive">{errors.fullName}</p>
              : fullNameNote && <p className="text-xs text-destructive">{fullNameNote}</p>}
          </div>

          {dobOn && (
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" className="text-sm font-medium">
                Date of Birth <span className="text-destructive">*</span>
              </Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={dateOfBirth}
                max={new Date().toISOString().split("T")[0]}
                onChange={e => setDateOfBirth(e.target.value)}
                aria-invalid={!!errors.dateOfBirth}
                className={cn("h-11 cursor-pointer", errors.dateOfBirth && "border-destructive focus-visible:ring-destructive")}
              />
              <p className="text-xs text-muted-foreground">Required to comply with India's DPDP Act</p>
              {errors.dateOfBirth && <p className="text-xs text-destructive">{errors.dateOfBirth}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="mobileNumber" className="text-sm font-medium">
              Phone <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <CountryCodeSelect value={country} onChange={setCountry} />
              <Input
                id="mobileNumber"
                type="tel"
                inputMode="numeric"
                value={mobileNumber}
                onChange={e => setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder="9876543210"
                maxLength={12}
                aria-invalid={!!errors.mobileNumber}
                className={cn("h-11 flex-1", errors.mobileNumber && "border-destructive focus-visible:ring-destructive")}
              />
            </div>
            {errors.mobileNumber && <p className="text-xs text-destructive">{errors.mobileNumber}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-invalid={!!errors.email}
              className={cn("h-11", errors.email && "border-destructive focus-visible:ring-destructive")}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Create a password"
              aria-invalid={!!errors.password}
              className={cn("h-11", errors.password && "border-destructive focus-visible:ring-destructive")}
            />
            {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirm Password <span className="text-destructive">*</span>
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repeat your password"
              aria-invalid={!!errors.confirmPassword}
              className={cn("h-11", errors.confirmPassword && "border-destructive focus-visible:ring-destructive")}
            />
            {errors.confirmPassword && <p className="text-xs text-destructive">{errors.confirmPassword}</p>}
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-11 rounded-full font-semibold">
            {submitting ? "Creating account…" : "Create account"}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By creating an account, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-foreground transition-colors">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</Link>.
          </p>
        </form>

        {/* Google sign-up hidden temporarily — do not remove
        <div className="my-6 flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignUp}
          className="w-full h-11 rounded-full"
        >
          Continue with Google
        </Button>
        */}

        <p className="mt-8 text-sm text-center text-muted-foreground">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-foreground underline hover:no-underline">
            Login
          </Link>
        </p>
      </div>
    </div>
  );
}
